import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Storage} from "@ionic/storage";

import {Observable} from 'rxjs/Observable';
import {ErrorObservable} from 'rxjs/observable/ErrorObservable';
import {catchError, tap} from 'rxjs/operators';
import "rxjs/add/operator/timeout";

import {Doctor} from "../../model/doctor";
import {BleGear, Location, Practice, ScanLayout, ScanProtocolSettings} from "../../model/practice";
import {getAge, Patient} from "../../model/patient";
import {Scan} from "../../model/scan";
import {ApiVersion, Box, KeyPerformanceIndicators, ScanTypes} from "../../model/weak-entities";
import {ClaStorage} from "../../model/events";
import {Exam} from "../../model/exam";
import {BehaviorSubject} from "rxjs";
import {of} from "rxjs/observable/of";
import { ScanViewUtils } from '../../components/scan-view/scan-view';
import {HRVScanData, ThermalScanData} from "../../model/scandata";

//const SERVICE_URL_US = "api.insightsynapse.com";
const SERVICE_URL_US = "synapse-api-us.azurewebsites.net";
//const SERVICE_URL_CA = "api.insightsynapse.ca";
const SERVICE_URL_CA = "synapse-api-ca.azurewebsites.net";
const SERVICE_URL_DEV = "claweb.azurewebsites.net";
const PROTOCOL = "https://";
const OFFLINE_URL = "192.168.137.1:5000";
const WEB_URL = "insightsynapse.com";
//const OFFLINE_URL = "localhost:5000";
// const PROTOCOL = "http://";

@Injectable()
export class ApiRestProvider {
  baseUrl:string = PROTOCOL + SERVICE_URL_US;
  public isOffline: boolean = false;
  private _isLocalOnline: boolean = false;
  public forgotPassUrlUS = `https://portal.insightsynapse.com/Identity/Account/ForgotPassword`;
  public forgotPassUrlINT = `https://portal.insightsynapse.ca/Identity/Account/ForgotPassword`;
  public forgotPassUrlDEV = `https://clawebportal.azurewebsites.net/Identity/Account/ForgotPassword`;
  public forgotPassUrl = this.currentServer === "US" ? `https://portal.insightsynapse.com/Identity/Account/ForgotPassword` : `https://portal.insightsynapse.ca/Identity/Account/ForgotPassword`

  public get currentServer(): string {
    if (this.baseUrl.includes(SERVICE_URL_CA)) {
      return "INT";
    } else if (this.baseUrl.includes(SERVICE_URL_DEV)) {
      return "DEV";
    }

    return "US";
  }

  public set currentServer(server:string) {
    if (server == 'INT') {
      this.baseUrl = PROTOCOL + SERVICE_URL_CA;
      this.storage.set(ClaStorage.APP_SERVER_USE, "INT");
      this.forgotPassUrl = `https://portal.insightsynapse.ca/Identity/Account/ForgotPassword`;
    } else if (server == 'DEV') {
      this.baseUrl = PROTOCOL + SERVICE_URL_DEV;
      this.storage.set(ClaStorage.APP_SERVER_USE, "DEV");
      this.forgotPassUrl = `https://clawebportal.azurewebsites.net/Identity/Account/ForgotPassword`;
    } else {
      this.baseUrl = PROTOCOL + SERVICE_URL_US;
      this.storage.set(ClaStorage.APP_SERVER_USE, "US");
      this.forgotPassUrl = `https://portal.insightsynapse.com/Identity/Account/ForgotPassword`;
    }
  }

  private get isLocalOnline(): boolean {
    return this._isLocalOnline;
  }
  private set isLocalOnline(state: boolean) {
    if(this._isLocalOnline == state) return;

    this._isLocalOnline = state;
    this.LocalOnlineState.next(state);
  }
  public LocalOnlineState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.isLocalOnline);
  private activeBox: Box;
  constructor(private http: HttpClient, private storage:Storage) {
    storage.get(ClaStorage.APP_SERVER_USE).then((server) => {

      // Make dev builds go straight to dev if not set
      if (process.env.CI_GIT_REF !== 'master' && !server) {
        //this.baseUrl = PROTOCOL + SERVICE_URL_DEV;
        //storage.set(ClaStorage.APP_SERVER_USE, "DEV");

        return;
      }

      if (!server || server == "US") {
        this.baseUrl = PROTOCOL + SERVICE_URL_US;

        if (!server) {
          storage.set(ClaStorage.APP_SERVER_USE, "US");
        }
      } else if (server == "DEV") {
        this.baseUrl = PROTOCOL + SERVICE_URL_DEV;
      } else {
        this.baseUrl = PROTOCOL + SERVICE_URL_CA;
      }
    });
  }

  public async useCloud() {
    let server = await this.storage.get(ClaStorage.APP_SERVER_USE);

    if (server == "INT") {
      this.baseUrl = PROTOCOL + SERVICE_URL_CA;
    } else if (server == "DEV") {
      this.baseUrl = PROTOCOL + SERVICE_URL_DEV;
    } else {
      this.baseUrl = PROTOCOL + SERVICE_URL_US;
    }

    this.isOffline = false;
    this.isLocalOnline = false;
  }

  public useOffline() {
    this.baseUrl = 'http://' + OFFLINE_URL;
    this.isOffline = true;
    this.isLocalOnline = false;
  }

  public useLocalOnline() {
    this.baseUrl = `https://${this.activeBox.DeviceId}.${WEB_URL}:5003`;
    //this.baseUrl = 'http://localhost:5001';
    this.isOffline = false;
    this.isLocalOnline = true;
  }

  // Only fall back to the bridge when the cloud is unreachable. HTTP errors the cloud
  // answered with (401/403/404/500...) would hit the bridge's copy of the same data.
  private shouldFallBackToLocal(err: any): boolean {
    if (this.isLocalOnline || this.isOffline) return false;

    const status = err instanceof HttpErrorResponse ? err.status : undefined;
    const unreachable = status === 0 || status === 502 || status === 503 || status === 504
      || (err && err.name === 'TimeoutError');

    console.warn(`[ApiRest] cloud request failed (status ${status}, ${err && (err.message || err)})` +
      (unreachable ? ' - switching to Local Online' : ''));
    return unreachable;
  }

  //region PING / CONNECTIVITY
  public async pingCloud() {
    try {
      let response = await this.http.get(PROTOCOL + SERVICE_URL_US, {responseType: 'text', observe: 'response'})
        .timeout(5000)
        .toPromise();
      return true;
    }
    catch(e) {
      return false;
    }
  }
  public async pingServer() {
    try {
      let response = await this.http.get(this.baseUrl, {responseType: 'text', observe: 'response'})
        .timeout(2000)
        .toPromise();
      return true;
    }
    catch(e) {
      return false;
    }
  }

  public async pingBox(id:string) {
    try {
      if(!this.activeBox) {
        this.activeBox = <Box>{DeviceId: id}
      } else if (this.activeBox.DeviceId != id) {
        this.activeBox.DeviceId = id;
      }
      let response = await this.http.get(`${this.baseUrl}/boxes/ping/${id}`, {responseType: 'text', observe: 'response'})
        .timeout(1000)
        .toPromise();
      return true;
    }
    catch(e) {
      return false;
    }
  }

  public async pingBoxLocal(box:Box, offline: boolean) {
    this.activeBox = box;
    let url = '';
    try {
      url = `https://${box.DeviceId}.${WEB_URL}:5002`;
      if(offline) {
        url = `http://${box.IpAddress}:5001`;
      }
      let response = await this.http.get(url, {responseType: 'text', observe: 'response'})
        .timeout(5000)
        .toPromise();
      box.SslFallback = false;
      return true;
    }
    catch(e) {
      return false;
    }
  }

  public async pingBoxLocalIp(box:Box, offline:boolean):Promise<boolean> {
    this.activeBox = box;
    let url = '';
    try {
      url = `http://${box.IpAddress}:5001`;
      let response = await this.http.get(url, {responseType: 'text', observe: 'response'})
        .timeout(5000)
        .toPromise();
      box.SslFallback = true;
      return true;
    }
    catch(e) {
      return false;
    }
  }
  //endregion

  //region ACCOUNT

  public login(cred: Credentials, server: string = "") : Observable<HttpResponse<string>> {

    let url = this.baseUrl;

    if (server == "US") {
      url = PROTOCOL + SERVICE_URL_US;
    } else if (server == "INT") {
      url = PROTOCOL + SERVICE_URL_CA;
    } else if (server == "DEV") {
      url = PROTOCOL + SERVICE_URL_DEV;
    }

    return this.http
      .post(`${url}/auth/token`, cred, {responseType: 'text', observe: 'response'})
      .pipe(
        tap(
          data => {
            this.storage.set(ClaStorage.APP_LOGIN_TOKEN, data.body);
          },
          error => {
            let err = error as HttpErrorResponse;
          }
        ))
  }

  //endregion

  //region DOCTOR

  public getDoctor() {
    return this.http
      .get<Doctor>(`${this.baseUrl}/doctor`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getDoctor();
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getDoctor')))
      );
  }

  public getDoctorRole() {
    return this.http
      .get<{role: string}>(`${this.baseUrl}/doctor/role`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getDoctorRole();
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getDoctorRole')))
      );
  }

  public getDoctorById(id:string) {
    return this.http
      .get<Doctor>(`${this.baseUrl}/doctor/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getDoctorById(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getDoctorById')))
      );
  }

  public modifyDoctor(doc:Doctor) {
    return this.http
      .patch(`${this.baseUrl}/doctor`, doc)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.modifyDoctor(doc);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'modifyDoctor')))
      );
  }

  //endregion

  //region PRACTICE

  public getPractice() {
    return this.http
      .get<Practice>(`${this.baseUrl}/practice`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getPractice();
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getPractice')))
      );
  }

  public getPracticeKpis(id:string) {
    return this.http
      .get<KeyPerformanceIndicators>(`${this.baseUrl}/practice/kpi/${id}`)
      .pipe(
        catchError((err) => {
          return[];
        })
      );
  }
  public getPracticeKpisWithFilter(id:string,startTimestamp: number, endTimestamp: number) {
    return this.http
      .get<KeyPerformanceIndicators>(`${this.baseUrl}/practice/kpi/${id}/${startTimestamp}/${endTimestamp}`)
      .pipe(
        catchError((err) => {
          return[];
        })
      );
  }
  public modifyPractice(prac:Practice) {
    return this.http
      .patch(`${this.baseUrl}/practice`, prac)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.modifyPractice(prac);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'modifyPractice')))
      );
  }

  public getLocationById(id:string) {
    return this.http
      .get<Location>(`${this.baseUrl}/practice/locations/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getLocationById(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getLocationById')))
      );
  }

  public addLocation(loc:Location) {
    return this.http
      .post<Location>(`${this.baseUrl}/practice/locations`, loc)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addLocation(loc);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addLocation')))
      );
  }

  public modifyLocation(loc:Location) {
    return this.http
      .patch(`${this.baseUrl}/practice/locations/${loc.Id}`, loc)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.modifyLocation(loc);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'modifyLocation')))
      );
  }

  public deleteLocation(id:string) {
    return this.http
      .delete(`${this.baseUrl}/practice/locations/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.deleteLocation(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deleteLocation')))
      );
  }

  public changePraticePhoto(photo:any) {
    let formData = new FormData();

    if (photo) {
      formData.append(photo.name, photo);
    }

    return this.http
      .post(`${this.baseUrl}/practice/photo`, formData)
      .pipe(catchError(err => of(this.handleError(err,'changePracticePhoto'))));
  }

  public changeDoctorPhoto(photo:any) {
    let formData = new FormData();

    if (photo) {
      formData.append(photo.name, photo);
    }

    return this.http
      .post(`${this.baseUrl}/doctor/photo`, formData)
      .pipe(catchError(err => of(this.handleError(err,'changeDoctorPhoto'))));
  }

  //endregion

  ///region SETTINGS

  public getScanProtocolSettings() {
    return this.http
      .get<ScanProtocolSettings[]>(`${this.baseUrl}/practice/settings/scanprotocols`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getScanProtocolSettings();
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getScanProtocolSettings')))
      );
  }

  public addScanProtocolSettings(protocol) {
    return this.http
      .post<ScanProtocolSettings>(`${this.baseUrl}/practice/settings/scanprotocols`, protocol)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addScanProtocolSettings(protocol);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addScanProtocolSettings')))
      );
  }

  public editScanProtocolSettings(protocol) {
    return this.http
      .patch(`${this.baseUrl}/practice/settings/scanprotocols/${protocol.Id}`, protocol)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.editScanProtocolSettings(protocol);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'editScanProtocolSettings')))
      );
  }

  public deleteScanProtocolSettings(id: string) {
    return this.http
      .delete(`${this.baseUrl}/practice/settings/scanprotocols/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.deleteScanProtocolSettings(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deleteScanProtocolSettings')))
      );
  }

  public getScanLayoutSettings() {
    return this.http
      .get<ScanLayout[]>(`${this.baseUrl}/practice/settings/scanlayouts`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getScanLayoutSettings();
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getScanLayoutSettings')))
      );
  }

  public addScanLayoutSettings(layout) {
    return this.http
      .post<ScanLayout>(`${this.baseUrl}/practice/settings/scanlayouts`, layout)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addScanLayoutSettings(layout);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addScanLayoutSettings')))
      );
  }

  public editScanLayoutSettings(layout) {
    return this.http
      .patch(`${this.baseUrl}/practice/settings/scanlayouts/${layout.Id}`, layout)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.editScanLayoutSettings(layout);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'editScanLayoutSettings')))
      );
  }

  public deleteScanLayoutSettings(id: string) {
    return this.http
      .delete(`${this.baseUrl}/practice/settings/scanlayouts/${id}`)
      .pipe(
        catchError((err) => {
        if(this.shouldFallBackToLocal(err)) {
          this.useLocalOnline();
          return this.deleteScanLayoutSettings(id);
        }
        throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deleteScanLayoutSettings')))
      );
  }

  ///endregion

  //region PATIENT

  public getPatientById(id:string) {
    return this.http
      .get<Patient>(`${this.baseUrl}/patients/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getPatientById(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getPatientById'))));
  }

  public getAllPatients(limit?:number, index?:number) {
    return this.http.get<Patient[]>(`${this.baseUrl}/patients${limit ? '?limit=' + limit : ''}${index ? '&index=' + index : ''}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getAllPatients(limit, index);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getAllPatients'))));
  }

  public getPatientsFilterByName(filter:string, limit?:number, index?:number) {
    return this.http.get<Patient[]>(`${this.baseUrl}/patients/filterbyname/${filter + (limit ? '?limit=' + limit : '') + (index ? '&index=' + index : '')}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getPatientsFilterByName(filter, limit, index);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getPatientsFilterByName'))));
  }

  public addPatient(pat:Patient) {
    return this.http
      .post<Patient>(`${this.baseUrl}/patients`, pat)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addPatient(pat);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addPatient'))));
  }

  public modifyPatient(pat:Patient) {
    return this.http
      .patch(`${this.baseUrl}/patients/${pat.Id}`, pat)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.modifyPatient(pat);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'modifyPatient'))));
  }

  public deletePatient(id:string) {
    return this.http
      .delete(`${this.baseUrl}/patients/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.deletePatient(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deletePatient')))
      );
  }

  public changePatientPhoto(id:string, photo:any) {
    if (!id) {
      return;
    }
    let formData = new FormData();

    if (photo) {
      formData.append(photo.name, photo);
    }

    return this.http
      .post(`${this.baseUrl}/patients/${id}/photo`, formData)
      .pipe(catchError(err => of(this.handleError(err,'changePatientPhoto'))));
  }

  public getAllScansForPatientWithId(id:string) {
    return this.http
      .get<Scan[]>(`${this.baseUrl}/patients/${id}/scans`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getAllScansForPatientWithId(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getAllScansForPatientWithId')))
      );
  }

  public getAllActiveScansForPatientWithId(id:string,date:Date) {
    return this.http
      .get<Scan[]>(`${this.baseUrl}/patients/${id}/activescans`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getAllActiveScansForPatientWithId(id,date);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getAllActiveScansForPatientWithId')))
      );
  }

  public getAllExamsForPatientWithId(id:string) {
    return this.http
      .get<Exam[]>(`${this.baseUrl}/patients/${id}/exams`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getAllExamsForPatientWithId(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getAllExamsForPatientWithId')))
      );
  }

  public getAllExamsNoFilterForPatientWithId(id:string) {
    return this.http
      .get<Exam[]>(`${this.baseUrl}/patients/${id}/examsnofilter`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getAllExamsNoFilterForPatientWithId(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getAllExamsNoFilterForPatientWithId')))
      );
  }

  //endregion

  //region SCANS

  public getScanById(id:string) {
    return this.http
      .get<Scan>(`${this.baseUrl}/scans/${id}`)
      .pipe(
        catchError((err : HttpErrorResponse) => {
          if(err.status != 500) this.handleError;

          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getScanById(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getScanById')))
      );
  }

  public deleteScan(id:string) {
    return this.http
      .delete(`${this.baseUrl}/scans/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.deleteScan(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deleteScan')))
      );
  }

  public addHrvScan(scan:Scan) {
    return this.http
      .post<Scan>(`${this.baseUrl}/scans/hrv`, scan)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addHrvScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addHrvScan')))
      );
  }

  public addEmgScan(scan:Scan) {
    return this.http
      .post<Scan>(`${this.baseUrl}/scans/emg`, scan)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addEmgScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addEmgScan')))
      );
  }

  public addThermalScan(scan:Scan) {
    return this.http
      .post<Scan>(`${this.baseUrl}/scans/thermal`, scan)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addThermalScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addThermalScan')))
      );
  }

  public addRomScan(scan:Scan) {
    return this.http
      .post<Scan>(`${this.baseUrl}/scans/rom`, scan)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addRomScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addRomScan')))
      );
  }

  public addAlgoScan(scan:Scan) {
    return this.http
      .post<Scan>(`${this.baseUrl}/scans/algo`, scan)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addAlgoScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addAlgoScan')))
      );
  }

  public moveScan(scan:Scan, isChild:boolean = false) {
    const practice:Practice =  this.getPractice().toPromise();
    let scanType = ScanTypes[scan.ScanType];
    let ScanScore = "";
    let ScanVersion = "";

    if(scanType == ScanTypes.EMG.toString()) {
      let emgBreakdown = ScanViewUtils.getEmgBreakdown(scan);
      if(scan.ScanData.length > 8) {
        ScanScore = emgBreakdown.Score.toString();
        ScanVersion = emgBreakdown.ScoreVersion;
      }
    }
    else if(scanType == ScanTypes.HRV.toString()) {
      let hrvBreakdown = ScanViewUtils.getHrvBreakdown(scan);
      let exam = true;
      for (const sd of scan.ScanData as HRVScanData[]) {
        if (sd.DCDuration < (180 * 1000) && isChild === false) {
          exam = false;
        }
      }
      if(exam) {
        ScanScore = hrvBreakdown.Score.toString();
        ScanVersion = hrvBreakdown.ScoreVersion;
      }
    }
    else if(scanType == ScanTypes.RollingThermal.toString() || scanType == ScanTypes.Thermal.toString()) {
      let thermalBreakdown = ScanViewUtils.getThermalBreakdown(scan, practice.DisableThermalShift);
      let exam = false;
      let cervical = true;
      let rolling = false;


      const length = scan.ScanData.length;

      for (const sd of scan.ScanData as ThermalScanData[]) {
        // check length
        if(!sd.SpinalLocation) {
          // rolling
          rolling = true;
        }
        // check if cervical
        if(sd.SpinalLocation && sd.SpinalLocation > 8) {
          cervical = false;
        }
      }

      // too small to be an exam
      if(rolling && length > 9) {
        exam = true;
      } else if (length > 8) {
        exam = true;
      }
      if(exam && !cervical) {
        ScanScore = thermalBreakdown.Score.toString();
        ScanVersion = thermalBreakdown.ScoreVersion;
      }
    }

    // ROM and Algo scans are not assigned to Exams

    return this.http
      .patch(`${this.baseUrl}/scans/${scan.Id}`, {NewPatientId: scan.PatientId, ScanId: scan.Id, ScanScore: ScanScore, ScanVersion: ScanVersion})
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.moveScan(scan);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'moveScan')))
      );
  }

  public isRollingScan(id:string) {
    return this.http
      .get<boolean>(`${this.baseUrl}/scans/isRolling/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.isRollingScan(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'isRollingScan')))
      );
  }

  //endregion

  //region Exam
  public addExam(exam:Exam) {
    return this.http
      .post<Exam>(`${this.baseUrl}/exam`, exam)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.addExam(exam);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'addExam')))
      );
  }

  public getExam(id: string) {
    return this.http
      .get<Exam>(`${this.baseUrl}/exam/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.getExam(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'getExam')))
      );
  }

  public modifyExam(exam:Exam) {
    return this.http
      .patch(`${this.baseUrl}/exam/${exam.Id}`, exam)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.modifyExam(exam);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'modifyExam')))
      );
  }

  public deleteExam(id:string) {
    return this.http
      .delete(`${this.baseUrl}/exam/${id}`)
      .pipe(
        catchError((err) => {
          if(this.shouldFallBackToLocal(err)) {
            this.useLocalOnline();
            return this.deleteExam(id);
          }
          throw('Failed');
        }),
        catchError(err => of(this.handleError(err,'deleteExam')))
      );
  }

  //endregion

  //region BOXES

  public getBoxes() {
    return this.http
      .get<Box[]>(`${this.baseUrl}/boxes`)
      .catch(err => of(this.squashError(err,'getBoxes',[])));
  }

  public getAllBoxes() {
    return this.http
      .get<Box[]>(`${this.baseUrl}/boxes/allboxes`)
      .catch(err => of(this.squashError(err,'getAllBoxes',[])));
  }

  public getBoxById(id:string) {
    return this.http
      .get<Box>(`${this.baseUrl}/boxes/${id}`)
      .pipe(catchError(err => of(this.squashError(err,'getBoxById',null))));
  }

  public provisionBox(id:string) {
    return this.http
      .get(`${this.baseUrl}/boxes/provision/${id}`)
      .pipe(catchError(err => of(this.handleError(err,'provisionBox'))));
  }

  public deprovisionBox(id:string) {
    return this.http
      .delete(`${this.baseUrl}/boxes/provision/${id}`)
      .pipe(catchError(err => of(this.handleError(err,'deprovisionBox'))));
  }

  public patchBox(box:Box) {
    return this.http
      .patch(`${this.baseUrl}/boxes`, box)
      .pipe(catchError(err => of(this.handleError(err,'patchBox'))));
  }

  //endregion

  //region BleTech
  public getBleGear() {
    return this.http
      .get<BleGear[]>(`${this.baseUrl}/gear/allgear`)
      .catch(err => of(this.squashError(err,'getBleTech',[])));
  }

  public addBleGear(gear: BleGear) {
    return this.http
      .post<BleGear>(`${this.baseUrl}/gear`, gear)
      .pipe(catchError(err => of(this.handleError(err,'addBleGear'))));
  }

  public deleteBleGear(id:string) {
    return this.http
      .delete(`${this.baseUrl}/gear/${id}`)
      .pipe(catchError(err => of(this.handleError(err,'deleteBleGear'))));
  }

  //endregion


  public getApiVersion(): Observable<ApiVersion> {
    let url = `https://${this.activeBox.DeviceId}.${WEB_URL}:5003`;
    if(this.isOffline) {
      url = this.baseUrl;
    }
    return this.http
      .get<ApiVersion>(`${url}/info/version`)
      .pipe(catchError(err => of(this.squashError(err,'getApiVersion',undefined))));
  }
   /*
  public syncStatus() {
    let url = `http://${this.activeBox.DeviceId}.${WEB_URL}:5000`;
    if(this.isOffline) {
      url = this.baseUrl;
    }
    return this.http
      .get<any[]>(`${url}/sync/status`)
      .pipe(catchError(err => of(this.squashError(err,'syncStatus',undefined))));
  }

  public syncResults() {
    let url = `http://${this.activeBox.DeviceId}.${WEB_URL}:5000`;
    if(this.isOffline) {
      url = this.baseUrl;
    }
    return this.http
      .get<any[]>(`${url}/sync/results`)
      .pipe(catchError(err => of(this.handleError(err,'syncResults'))));
  }
  */

  private handleError(err: any,name: string) {
    const error = err as HttpErrorResponse;
    let errStr:string = "Something bad happened; Please try again later.";

    if (error.error instanceof ErrorEvent) {
      //Clientside network error
      errStr = "Unable to reach server. Please check your network connection."
    } else {
      //Backend error
      switch (error.status) {
        case 403:
          errStr = "You do not have access to this resource";
          break;

        case 401:
          errStr = "The email or password you entered was invalid";
          break;

        default:
          errStr = "The server was unable to handle your request; Please try again later";
          break;

      }
    }

    return new ErrorObservable(errStr);
  }

  private squashError(err: any, name: string, result: any)
  {
    const error = err as HttpErrorResponse;
    return result;
  }

}

export interface Credentials {
  Email:string,
  Password:string
}



// WEBPACK FOOTER //
// ./src/providers/api-rest/api-rest.ts